import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "@/hooks/useAuth";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import {
  Plus, Trash, TrendUp, TrendDown, CircleNotch, MagnifyingGlass,
  CheckCircle, XCircle, WarningCircle, CurrencyDollar, ChartPie,
  ArrowCounterClockwise, CaretDown, CaretUp, Briefcase, CloudArrowUp
} from "@phosphor-icons/react";
import UpgradeModal from "./UpgradeModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = "portfolio_holdings";
const FREE_LIMIT = 5;

const VERDICT_COLORS = {
  compliant: { bg: "bg-green-500/10", text: "text-green-500", border: "border-green-500/30", label: "Compliant" },
  borderline: { bg: "bg-amber-500/10", text: "text-amber-500", border: "border-amber-500/30", label: "Borderline" },
  non_compliant: { bg: "bg-red-500/10", text: "text-red-500", border: "border-red-500/30", label: "Non-Compliant" },
  unknown: { bg: "bg-zinc-500/10", text: "text-zinc-500", border: "border-zinc-500/30", label: "Unknown" },
};

function VerdictBadge({ verdict }) {
  const v = VERDICT_COLORS[verdict] || VERDICT_COLORS.unknown;
  return (
    <span className={`px-2 py-0.5 text-[9px] font-mono font-bold uppercase tracking-widest border ${v.bg} ${v.text} ${v.border}`} data-testid={`verdict-badge-${verdict}`}>
      {v.label}
    </span>
  );
}

function RatioBar({ label, value, threshold, status }) {
  const pct = Math.min((value / threshold) * 100, 100);
  const color = status === "pass" ? "bg-green-500" : status === "borderline" ? "bg-amber-500" : status === "fail" ? "bg-red-500" : "bg-zinc-500";
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-[10px] font-mono">
        <span className="text-zinc-500 dark:text-zinc-400">{label}</span>
        <span className="text-zinc-700 dark:text-zinc-300">{value}% <span className="text-zinc-400 dark:text-zinc-600">/ {threshold}%</span></span>
      </div>
      <div className="h-1 bg-zinc-100 dark:bg-zinc-800 overflow-hidden">
        <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function HoldingCard({ holding, onRemove, onSelect, expanded, onToggle }) {
  const isUp = holding.change >= 0;

  return (
    <div
      className="border border-zinc-200 dark:border-zinc-800 transition-colors hover:border-zinc-300 dark:hover:border-zinc-700"
      data-testid={`holding-card-${holding.ticker}`}
    >
      <div
        className="flex items-center justify-between p-4 cursor-pointer"
        onClick={() => onToggle(holding.ticker)}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-mono font-medium text-zinc-950 dark:text-zinc-100">{holding.ticker}</span>
              <VerdictBadge verdict={holding.verdict} />
            </div>
            <p className="text-[11px] text-zinc-500 dark:text-zinc-400 truncate mt-0.5">{holding.company_name}</p>
          </div>
        </div>

        <div className="flex items-center gap-4 shrink-0">
          <div className="text-right">
            <p className="text-sm font-mono text-zinc-950 dark:text-zinc-100">${holding.price.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
            <p className={`text-[11px] font-mono flex items-center justify-end gap-0.5 ${isUp ? "text-green-500" : "text-red-500"}`}>
              {isUp ? <TrendUp size={10} weight="bold" /> : <TrendDown size={10} weight="bold" />}
              {isUp ? "+" : ""}{holding.change_pct.toFixed(2)}%
            </p>
          </div>
          <div className="text-right hidden sm:block">
            <p className="text-[10px] tracking-[0.15em] uppercase font-mono text-zinc-400 dark:text-zinc-500">Value</p>
            <p className="text-sm font-mono text-zinc-950 dark:text-zinc-100">${holding.value.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
          </div>
          <div className="text-right hidden sm:block">
            <p className="text-[10px] tracking-[0.15em] uppercase font-mono text-zinc-400 dark:text-zinc-500">Shares</p>
            <p className="text-sm font-mono text-zinc-700 dark:text-zinc-300">{holding.shares}</p>
          </div>
          {expanded ? <CaretUp size={14} className="text-zinc-400" /> : <CaretDown size={14} className="text-zinc-400" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-zinc-100 dark:border-zinc-800 px-4 py-3 space-y-3">
          <div className="sm:hidden flex items-center gap-4 text-[11px] font-mono text-zinc-500 dark:text-zinc-400">
            <span>Shares: <span className="text-zinc-900 dark:text-zinc-200">{holding.shares}</span></span>
            <span>Value: <span className="text-zinc-900 dark:text-zinc-200">${holding.value.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <RatioBar label="Debt/MCap" value={holding.debt_ratio} threshold={33} status={holding.debt_status} />
            <RatioBar label="Interest/Rev" value={holding.interest_ratio} threshold={5} status={holding.interest_status} />
            <RatioBar label="AR/Assets" value={holding.receivable_ratio} threshold={49} status={holding.receivable_status} />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-[10px] font-mono text-zinc-400 dark:text-zinc-500">
              <span>{holding.sector}</span>
              {holding.market_cap > 0 && (
                <>
                  <span className="text-zinc-300 dark:text-zinc-700">|</span>
                  <span>MCap ${(holding.market_cap / 1e9).toFixed(1)}B</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); onSelect(holding); }}
                className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-200 transition-colors px-2 py-1"
                data-testid={`holding-detail-${holding.ticker}`}
              >
                Details
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onRemove(holding.ticker); }}
                className="p-1.5 text-zinc-400 hover:text-red-500 transition-colors"
                data-testid={`holding-remove-${holding.ticker}`}
              >
                <Trash size={14} weight="bold" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── sessionStorage helpers (anonymous users get a fresh portfolio each session) ─── */
function getLocalHoldings() {
  try {
    return JSON.parse(sessionStorage.getItem(LS_KEY) || "[]");
  } catch { return []; }
}
function setLocalHoldings(holdings) {
  sessionStorage.setItem(LS_KEY, JSON.stringify(holdings));
}

/* ─── Enrich a local holding with live data from /api/stocks/lookup ─── */
async function enrichHolding(h) {
  try {
    const [lookupRes, quoteRes] = await Promise.all([
      axios.get(`${API}/stocks/lookup/${h.ticker}`),
      axios.get(`${API}/stocks/${h.ticker}/quote`).catch(() => ({ data: {} })),
    ]);
    const stock = lookupRes.data;
    const quote = quoteRes.data || {};
    const price = quote.price || 0;
    const prevClose = quote.previous_close || price;
    const change = price - prevClose;
    const changePct = prevClose ? (change / prevClose) * 100 : 0;
    return {
      ticker: h.ticker,
      shares: h.shares,
      added_at: h.added_at || "",
      company_name: stock.company_name || h.ticker,
      sector: stock.sector || "Unknown",
      price,
      change: Math.round(change * 100) / 100,
      change_pct: Math.round(changePct * 100) / 100,
      value: Math.round(price * h.shares * 100) / 100,
      verdict: stock.verdict || "unknown",
      debt_ratio: stock.debt_ratio || 0,
      interest_ratio: stock.interest_ratio || 0,
      receivable_ratio: stock.receivable_ratio || 0,
      debt_status: stock.debt_status || "unknown",
      interest_status: stock.interest_status || "unknown",
      receivable_status: stock.receivable_status || "unknown",
      explanation: stock.explanation || "",
      market_cap: stock.market_cap || 0,
    };
  } catch {
    return {
      ticker: h.ticker, shares: h.shares, added_at: h.added_at || "",
      company_name: h.ticker, sector: "Unknown", price: 0,
      change: 0, change_pct: 0, value: 0, verdict: "unknown",
      debt_ratio: 0, interest_ratio: 0, receivable_ratio: 0,
      debt_status: "unknown", interest_status: "unknown", receivable_status: "unknown",
      explanation: "", market_cap: 0,
    };
  }
}

function buildSummary(enriched) {
  const total = enriched.length;
  if (!total) return null;
  const totalValue = enriched.reduce((s, e) => s + e.value, 0);
  const compliant = enriched.filter(e => e.verdict === "compliant");
  const borderline = enriched.filter(e => e.verdict === "borderline");
  const nonCompliant = enriched.filter(e => e.verdict !== "compliant" && e.verdict !== "borderline");
  return {
    total_value: Math.round(totalValue * 100) / 100,
    total_holdings: total,
    compliant: compliant.length,
    borderline: borderline.length,
    non_compliant: nonCompliant.length,
    compliant_pct: Math.round(compliant.length / total * 1000) / 10,
    borderline_pct: Math.round(borderline.length / total * 1000) / 10,
    non_compliant_pct: Math.round(nonCompliant.length / total * 1000) / 10,
    compliant_value: Math.round(compliant.reduce((s, e) => s + e.value, 0) * 100) / 100,
    borderline_value: Math.round(borderline.reduce((s, e) => s + e.value, 0) * 100) / 100,
    non_compliant_value: Math.round(nonCompliant.reduce((s, e) => s + e.value, 0) * 100) / 100,
  };
}


const SECTOR_COLORS = [
  "#10b981", "#3b82f6", "#8b5cf6", "#f59e0b", "#ec4899",
  "#06b6d4", "#f97316", "#6366f1", "#14b8a6", "#e11d48",
  "#84cc16", "#a855f7",
];

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div className="bg-zinc-900 border border-zinc-700 px-2.5 py-1.5 text-[10px] font-mono shadow-lg">
      <p className="text-zinc-100">{d.name}</p>
      <p className="text-zinc-400">{d.value}%</p>
    </div>
  );
}

function PortfolioCharts({ summary, holdings }) {
  const complianceData = useMemo(() => {
    const items = [];
    if (summary.compliant > 0) items.push({ name: "Compliant", value: summary.compliant_pct, color: "#0ea5e9" });
    if (summary.borderline > 0) items.push({ name: "Borderline", value: summary.borderline_pct, color: "#f59e0b" });
    if (summary.non_compliant > 0) items.push({ name: "Non-Compliant", value: summary.non_compliant_pct, color: "#f97316" });
    return items;
  }, [summary]);

  const sectorData = useMemo(() => {
    const map = {};
    holdings.forEach((h) => {
      const sec = h.sector || "Unknown";
      map[sec] = (map[sec] || 0) + h.value;
    });
    const totalVal = Object.values(map).reduce((a, b) => a + b, 0);
    if (totalVal === 0) return [];
    return Object.entries(map)
      .map(([name, val], i) => ({ name, value: Math.round(val / totalVal * 1000) / 10, color: SECTOR_COLORS[i % SECTOR_COLORS.length] }))
      .sort((a, b) => b.value - a.value);
  }, [holdings]);

  if (complianceData.length === 0 && sectorData.length === 0) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="portfolio-charts">
      {/* Compliance Donut */}
      <div className="border border-zinc-200 dark:border-zinc-800 p-4">
        <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-zinc-400 dark:text-zinc-500 mb-2">Compliance Breakdown</p>
        <div className="relative" style={{ height: 180 }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={100}>
            <PieChart>
              <Pie
                data={complianceData}
                cx="50%"
                cy="50%"
                innerRadius={52}
                outerRadius={76}
                paddingAngle={2}
                dataKey="value"
                strokeWidth={0}
              >
                {complianceData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<ChartTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-xl font-light font-mono text-zinc-950 dark:text-zinc-100">{summary.compliant_pct}%</span>
            <span className="text-[9px] font-mono text-zinc-400 dark:text-zinc-500 uppercase tracking-wider">Compliant</span>
          </div>
        </div>
        <div className="flex items-center justify-center gap-3 mt-1">
          {complianceData.map((d) => (
            <span key={d.name} className="flex items-center gap-1.5 text-[9px] font-mono text-zinc-500 dark:text-zinc-400">
              <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: d.color }} />
              {d.name}
            </span>
          ))}
        </div>
      </div>

      {/* Sector Allocation Donut */}
      <div className="border border-zinc-200 dark:border-zinc-800 p-4">
        <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-zinc-400 dark:text-zinc-500 mb-2">Sector Allocation</p>
        {sectorData.length > 0 ? (
          <>
            <div className="relative" style={{ height: 180 }}>
              <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={100}>
                <PieChart>
                  <Pie
                    data={sectorData}
                    cx="50%"
                    cy="50%"
                    innerRadius={52}
                    outerRadius={76}
                    paddingAngle={2}
                    dataKey="value"
                    strokeWidth={0}
                  >
                    {sectorData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-xl font-light font-mono text-zinc-950 dark:text-zinc-100">{sectorData.length}</span>
                <span className="text-[9px] font-mono text-zinc-400 dark:text-zinc-500 uppercase tracking-wider">{sectorData.length === 1 ? "Sector" : "Sectors"}</span>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 mt-1">
              {sectorData.slice(0, 6).map((d) => (
                <span key={d.name} className="flex items-center gap-1.5 text-[9px] font-mono text-zinc-500 dark:text-zinc-400">
                  <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: d.color }} />
                  {d.name.length > 14 ? d.name.slice(0, 12) + "…" : d.name}
                </span>
              ))}
              {sectorData.length > 6 && (
                <span className="text-[9px] font-mono text-zinc-500 dark:text-zinc-400">+{sectorData.length - 6} more</span>
              )}
            </div>
          </>
        ) : (
          <div className="h-[180px] flex items-center justify-center">
            <p className="text-[10px] font-mono text-zinc-400 dark:text-zinc-500">No sector data</p>
          </div>
        )}
      </div>
    </div>
  );
}


export default function PortfolioTracker({ onSelectStock }) {
  const { user, showAuthModal, isPremium } = useAuth();
  const isLoggedIn = !!user;

  const [holdings, setHoldings] = useState([]);
  const [summary, setSummary] = useState(null);
  const [tier, setTier] = useState("free");
  const [limit, setLimit] = useState(FREE_LIMIT);
  const [riskLevel, setRiskLevel] = useState("low");
  const [riskMessage, setRiskMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [tickerInput, setTickerInput] = useState("");
  const [sharesInput, setSharesInput] = useState("");
  const [error, setError] = useState("");
  const [expandedTicker, setExpandedTicker] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeTrigger, setUpgradeTrigger] = useState("");

  /* ─── Fetch portfolio (API if logged in, localStorage if not) ─── */
  const fetchPortfolio = useCallback(async () => {
    setLoading(true);
    try {
      if (isLoggedIn) {
        const res = await axios.get(`${API}/portfolio/tracker`, { withCredentials: true });
        setHoldings(res.data.holdings || []);
        setSummary(res.data.summary || null);
        setTier(res.data.tier || "free");
        setLimit(res.data.limit || FREE_LIMIT);
        setRiskLevel(res.data.risk_level || "low");
        setRiskMessage(res.data.risk_message || "");
      } else {
        const local = getLocalHoldings();
        if (local.length === 0) {
          setHoldings([]);
          setSummary(null);
          setTier("free");
          setLimit(FREE_LIMIT);
        } else {
          const enriched = await Promise.all(local.map(enrichHolding));
          setHoldings(enriched);
          setSummary(buildSummary(enriched));
          setTier("free");
          setLimit(FREE_LIMIT);
          // Compute local risk level
          const nc = enriched.filter(e => e.verdict !== "compliant" && e.verdict !== "borderline").length;
          const bl = enriched.filter(e => e.verdict === "borderline").length;
          if (nc > 0) {
            setRiskLevel("high");
            setRiskMessage(`You currently hold ${nc} non-compliant stock${nc > 1 ? "s" : ""}. Review recommended.`);
          } else if (bl > 0) {
            setRiskLevel("medium");
            setRiskMessage(`You have ${bl} borderline stock${bl > 1 ? "s" : ""} that may change compliance status.`);
          } else {
            setRiskLevel("low");
            setRiskMessage("All your holdings are currently halal-compliant.");
          }
        }
      }
    } catch (e) {
      if (e.response?.status !== 401) {
        setError("Failed to load portfolio");
      }
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    fetchPortfolio();
  }, [fetchPortfolio]);

  // Ticker search suggestions
  useEffect(() => {
    if (!tickerInput.trim() || tickerInput.trim().length < 1) {
      setSuggestions([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/stocks/search-fmp`, { params: { query: tickerInput.trim() } });
        setSuggestions((res.data || []).slice(0, 5));
        setShowSuggestions(true);
      } catch {
        setSuggestions([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [tickerInput]);

  const handleAdd = async (tickerOverride) => {
    const ticker = (tickerOverride || tickerInput).trim().toUpperCase();
    const shares = parseFloat(sharesInput);
    if (!ticker) { setError("Enter a ticker symbol"); return; }
    if (!shares || shares <= 0) { setError("Enter a valid number of shares"); return; }

    setAdding(true);
    setError("");
    setShowSuggestions(false);

    try {
      if (isLoggedIn) {
        await axios.post(`${API}/portfolio/holdings`, { ticker, shares }, { withCredentials: true });
      } else {
        const local = getLocalHoldings();
        const existing = local.findIndex(h => h.ticker === ticker);
        if (existing >= 0) {
          local[existing].shares = shares;
        } else {
          if (local.length >= FREE_LIMIT) {
            setUpgradeTrigger("This stock will NOT be monitored for compliance changes on the free plan. Upgrade to ensure your entire portfolio stays compliant without manual checking.");
            setUpgradeOpen(true);
            setAdding(false);
            return;
          }
          // Verify ticker exists
          try {
            await axios.get(`${API}/stocks/lookup/${ticker}`);
          } catch {
            setError(`Ticker '${ticker}' not found`);
            setAdding(false);
            return;
          }
          local.push({ ticker, shares, added_at: new Date().toISOString() });
        }
        setLocalHoldings(local);
      }
      setTickerInput("");
      setSharesInput("");
      await fetchPortfolio();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to add holding");
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (ticker) => {
    try {
      if (isLoggedIn) {
        await axios.delete(`${API}/portfolio/holdings/${ticker}`, { withCredentials: true });
      } else {
        const local = getLocalHoldings().filter(h => h.ticker !== ticker);
        setLocalHoldings(local);
      }
      await fetchPortfolio();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to remove holding");
    }
  };

  const handleSelectSuggestion = (s) => {
    setTickerInput(s.ticker);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const toggleExpand = (ticker) => {
    setExpandedTicker(prev => prev === ticker ? null : ticker);
  };

  if (loading) {
    return (
      <div className="py-16 flex flex-col items-center justify-center" data-testid="portfolio-tracker-loading">
        <CircleNotch size={24} weight="bold" className="animate-spin text-zinc-400 dark:text-zinc-500" />
        <p className="text-xs font-mono text-zinc-400 dark:text-zinc-500 mt-3">Loading portfolio...</p>
      </div>
    );
  }

  const s = summary;
  const slotsUsed = holdings.length;
  const slotsLeft = limit - slotsUsed;

  return (
    <div className="py-6 space-y-6" data-testid="portfolio-tracker">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium text-zinc-950 dark:text-zinc-100">Portfolio Tracker</h2>
          <p className="text-[11px] font-mono text-zinc-400 dark:text-zinc-500 mt-0.5">
            {slotsUsed} of {limit} stocks actively monitored
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!isLoggedIn && holdings.length > 0 && (
            <button
              onClick={() => showAuthModal()}
              className="flex items-center gap-1.5 px-2 py-1 text-[10px] font-mono uppercase tracking-wider text-emerald-600 dark:text-emerald-400 border border-emerald-600/30 dark:border-emerald-400/30 hover:bg-emerald-500/5 transition-colors"
              data-testid="save-portfolio-btn"
              title="Sign in to save your portfolio across devices"
            >
              <CloudArrowUp size={12} weight="bold" />
              Save
            </button>
          )}
          {tier === "free" && (
            <button
              onClick={() => { setUpgradeTrigger("Unlock full portfolio monitoring"); setUpgradeOpen(true); }}
              className="flex items-center gap-1.5 px-2 py-1 text-[10px] font-mono uppercase tracking-wider text-emerald-600 dark:text-emerald-400 border border-emerald-600/30 dark:border-emerald-400/30 hover:bg-emerald-500/5 transition-colors"
              data-testid="portfolio-upgrade-btn"
            >
              Upgrade
            </button>
          )}
          <button
            onClick={fetchPortfolio}
            className="p-1.5 border border-zinc-200 dark:border-zinc-800 text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors"
            data-testid="portfolio-refresh-btn"
            title="Refresh prices"
          >
            <ArrowCounterClockwise size={14} weight="bold" />
          </button>
        </div>
      </div>

      {/* Risk Level + Dynamic Message */}
      {holdings.length > 0 && (
        <div className={`flex items-start gap-3 px-3 py-2.5 border ${
          riskLevel === "high"
            ? "border-red-200 dark:border-red-900 bg-red-50/50 dark:bg-red-950/20"
            : riskLevel === "medium"
              ? "border-amber-200 dark:border-amber-900 bg-amber-50/50 dark:bg-amber-950/20"
              : "border-green-200 dark:border-green-900 bg-green-50/50 dark:bg-green-950/20"
        }`} data-testid="portfolio-risk-level">
          <div className={`w-2 h-2 rounded-full mt-1 shrink-0 ${
            riskLevel === "high" ? "bg-red-500" : riskLevel === "medium" ? "bg-amber-500" : "bg-green-500"
          }`} />
          <div>
            <p className={`text-[10px] font-mono uppercase tracking-wider font-medium ${
              riskLevel === "high" ? "text-red-700 dark:text-red-400" : riskLevel === "medium" ? "text-amber-700 dark:text-amber-400" : "text-green-700 dark:text-green-400"
            }`}>
              {riskLevel === "high" ? "High Risk" : riskLevel === "medium" ? "Medium Risk" : "Low Risk"} Portfolio
            </p>
            <p className="text-[11px] font-mono text-zinc-500 dark:text-zinc-400 mt-0.5" data-testid="risk-message">
              {riskMessage}
            </p>
          </div>
        </div>
      )}

      {/* Free tier monitoring warning */}
      {tier === "free" && slotsLeft <= 1 && holdings.length > 0 && (
        <div className="flex items-start gap-2 px-3 py-2 border border-amber-200 dark:border-amber-900 bg-amber-50/30 dark:bg-amber-950/10" data-testid="monitoring-limit-warning">
          <WarningCircle size={14} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />
          <p className="text-[10px] font-mono text-amber-700 dark:text-amber-400">
            {slotsLeft === 0
              ? "Additional holdings will NOT be tracked for compliance changes."
              : `Only ${slotsLeft} monitoring slot remaining.`}
            {" "}
            <button onClick={() => { setUpgradeTrigger("Upgrade to ensure your entire portfolio stays compliant without manual checking."); setUpgradeOpen(true); }} className="underline hover:no-underline">
              Upgrade for unlimited monitoring
            </button>
          </p>
        </div>
      )}

      {/* Local storage notice */}
      {!isLoggedIn && holdings.length > 0 && (
        <div className="flex items-start gap-2 px-3 py-2 border border-zinc-200 dark:border-zinc-800 text-[11px] font-mono text-zinc-400 dark:text-zinc-500" data-testid="local-storage-notice">
          <CloudArrowUp size={14} weight="bold" className="shrink-0 mt-0.5" />
          <span>Portfolio saved locally in this browser. <button onClick={() => showAuthModal()} className="underline text-zinc-600 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors">Sign in</button> to save across devices.</span>
        </div>
      )}

      {/* Add Holding Form */}
      <div className="border border-zinc-200 dark:border-zinc-800 p-4" data-testid="add-holding-form">
        <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-zinc-400 dark:text-zinc-500 mb-3">Add Holding</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <div className="flex items-center border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900">
              <MagnifyingGlass size={14} className="text-zinc-400 ml-3" />
              <input
                type="text"
                value={tickerInput}
                onChange={(e) => setTickerInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => { if (e.key === "Enter") handleAdd(); }}
                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                placeholder="Ticker (e.g. AAPL)"
                className="w-full px-2 py-2 text-sm font-mono bg-transparent focus:outline-none text-zinc-950 dark:text-zinc-100 placeholder:text-zinc-300 dark:placeholder:text-zinc-600"
                data-testid="add-holding-ticker"
              />
            </div>
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute z-50 top-full left-0 right-0 mt-1 border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 max-h-48 overflow-y-auto" data-testid="ticker-suggestions">
                {suggestions.map((s) => (
                  <button
                    key={s.ticker}
                    onClick={() => handleSelectSuggestion(s)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors flex items-center justify-between"
                    data-testid={`suggestion-${s.ticker}`}
                  >
                    <span className="font-mono text-zinc-950 dark:text-zinc-100">{s.ticker}</span>
                    <span className="text-[11px] text-zinc-500 dark:text-zinc-400 truncate ml-2">{s.company_name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <input
            type="number"
            value={sharesInput}
            onChange={(e) => setSharesInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleAdd(); }}
            placeholder="Shares"
            min="0.01"
            step="0.01"
            className="w-full sm:w-28 border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-zinc-950 dark:focus:ring-zinc-400 text-zinc-950 dark:text-zinc-100 placeholder:text-zinc-300 dark:placeholder:text-zinc-600"
            data-testid="add-holding-shares"
          />
          <button
            onClick={() => {
              if (!isPremium && slotsLeft <= 0) {
                setUpgradeTrigger("This stock will NOT be monitored for compliance changes on the free plan. Upgrade to ensure your entire portfolio stays compliant without manual checking.");
                setUpgradeOpen(true);
                return;
              }
              handleAdd();
            }}
            disabled={adding || !tickerInput.trim() || !sharesInput}
            className="flex items-center justify-center gap-1.5 px-4 py-2 text-[11px] font-mono uppercase tracking-wider bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 hover:bg-zinc-700 dark:hover:bg-zinc-300 transition-colors disabled:opacity-40 shrink-0"
            data-testid="add-holding-btn"
          >
            {adding ? <CircleNotch size={14} weight="bold" className="animate-spin" /> : <Plus size={14} weight="bold" />}
            {adding ? "Adding..." : "Add"}
          </button>
        </div>
        {error && (
          <p className="text-[10px] font-mono text-red-500 mt-2" data-testid="portfolio-error">{error}</p>
        )}
      </div>

      {/* Summary Cards */}
      {s && s.total_holdings > 0 && (
        <div className="space-y-4" data-testid="portfolio-summary">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="border border-zinc-200 dark:border-zinc-800 p-4">
              <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-zinc-400 dark:text-zinc-500">Portfolio Value</p>
              <p className="text-xl font-light font-mono text-zinc-950 dark:text-zinc-100 mt-1" data-testid="portfolio-total-value">
                ${s.total_value.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="border border-zinc-200 dark:border-zinc-800 p-4">
              <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-zinc-400 dark:text-zinc-500">Compliant</p>
              <p className="text-xl font-light font-mono text-green-500 mt-1">{s.compliant}</p>
              <p className="text-[10px] font-mono text-zinc-400 dark:text-zinc-500">{s.compliant_pct}%</p>
            </div>
            <div className="border border-zinc-200 dark:border-zinc-800 p-4">
              <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-zinc-400 dark:text-zinc-500">Borderline</p>
              <p className="text-xl font-light font-mono text-amber-500 mt-1">{s.borderline}</p>
              <p className="text-[10px] font-mono text-zinc-400 dark:text-zinc-500">{s.borderline_pct}%</p>
            </div>
            <div className="border border-zinc-200 dark:border-zinc-800 p-4">
              <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-zinc-400 dark:text-zinc-500">Non-Compliant</p>
              <p className="text-xl font-light font-mono text-red-500 mt-1">{s.non_compliant}</p>
              <p className="text-[10px] font-mono text-zinc-400 dark:text-zinc-500">{s.non_compliant_pct}%</p>
            </div>
          </div>

          {/* Compliance Bar */}
          <div data-testid="portfolio-compliance-bar">
            <div className="flex h-2.5 overflow-hidden">
              {s.compliant > 0 && <div className="bg-green-500" style={{ width: `${s.compliant_pct}%` }} />}
              {s.borderline > 0 && <div className="bg-amber-500" style={{ width: `${s.borderline_pct}%` }} />}
              {s.non_compliant > 0 && <div className="bg-red-500" style={{ width: `${s.non_compliant_pct}%` }} />}
            </div>
            <div className="flex items-center gap-4 mt-1.5">
              <span className="flex items-center gap-1.5 text-[10px] font-mono text-zinc-500 dark:text-zinc-400">
                <span className="w-2 h-2 bg-green-500 inline-block" /> Compliant ${s.compliant_value.toLocaleString()}
              </span>
              <span className="flex items-center gap-1.5 text-[10px] font-mono text-zinc-500 dark:text-zinc-400">
                <span className="w-2 h-2 bg-amber-500 inline-block" /> Borderline ${s.borderline_value.toLocaleString()}
              </span>
              <span className="flex items-center gap-1.5 text-[10px] font-mono text-zinc-500 dark:text-zinc-400">
                <span className="w-2 h-2 bg-red-500 inline-block" /> Non-Compliant ${s.non_compliant_value.toLocaleString()}
              </span>
            </div>
          </div>

          {/* Donut Charts */}
          <PortfolioCharts summary={s} holdings={holdings} />
        </div>
      )}

      {/* Holdings List */}
      {holdings.length > 0 ? (
        <div className="space-y-2" data-testid="holdings-list">
          <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-zinc-400 dark:text-zinc-500">
            Holdings ({holdings.length})
          </p>
          <div className="space-y-1">
            {holdings.map((h) => (
              <HoldingCard
                key={h.ticker}
                holding={h}
                onRemove={handleRemove}
                onSelect={onSelectStock}
                expanded={expandedTicker === h.ticker}
                onToggle={toggleExpand}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="py-12 text-center border border-dashed border-zinc-200 dark:border-zinc-800" data-testid="empty-portfolio">
          <Briefcase size={32} weight="thin" className="mx-auto text-zinc-300 dark:text-zinc-700 mb-3" />
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-1">No holdings yet</p>
          <p className="text-[11px] font-mono text-zinc-400 dark:text-zinc-500">
            Add your first stock above to start tracking compliance
          </p>
        </div>
      )}

      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} trigger={upgradeTrigger} />
    </div>
  );
}
